import { useAuth } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";

const BASE_API_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Hook that returns an authenticated fetch wrapper.
 * Automatically attaches the Clerk JWT to every request.
 */
export const useAuthFetch = () => {
  const { getToken } = useAuth();

  const authFetch = useCallback(
    async (path, options = {}) => {
      const token = await getToken();
      return fetch(`${BASE_API_URL}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });
    },
    [getToken],
  );

  return authFetch;
};

/**
 * Fetches the current user from your backend.
 * Accepts a `ready` flag so it waits until sync is complete.
 */
// In useMe, return a refetch function
export const useMe = (ready = true) => {
  const { getToken, isSignedIn } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }
    try {
      const token = await getToken();
      const res = await fetch(`${BASE_API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMe(await res.json());
    } catch (err) {
      console.error("[useMe] Failed:", err);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (!ready) {
      setLoading(false);
      return;
    }
    load();
  }, [ready, load]);

  return { me, loading, refetch: load };
};
/**
 * Syncs a Clerk user with your backend on sign-in.
 * Creates the user if they don't exist yet.
 */
export const syncUserWithBackend = async (clerkUser, getToken) => {
  if (!clerkUser) return;

  const token = await getToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const payload = {
    clerkId: clerkUser.id,
    email: clerkUser.primaryEmailAddress?.emailAddress ?? null,
    firstName: clerkUser.firstName ?? null,
    lastName: clerkUser.lastName ?? null,
    imageUrl: clerkUser.imageUrl ?? null,
  };

  try {
    const getRes = await fetch(`${BASE_API_URL}/users/me`, { headers });

    if (getRes.ok) return await getRes.json();

    if (getRes.status === 404) {
      const createRes = await fetch(`${BASE_API_URL}/users`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!createRes.ok)
        throw new Error(`Failed to create user: ${createRes.status}`);
      return await createRes.json();
    }

    throw new Error(`Unexpected response: ${getRes.status}`);
  } catch (err) {
    console.error("[clerk.service] User sync failed:", err);
  }
};
